/**
 * Custom in-viewport implementation using IntersectionObserver and MutationObserver
 * Replaces the in-viewport npm package
 */

interface InViewportOptions {
  offset?: number;
  container?: Element;
  debounce?: number;
}

type InViewportCallback = (element: Element) => void;

interface Watcher {
  watch: () => void;
  dispose: () => void;
}

/**
 * Creates an IntersectionObserver with offset support
 */
function createObserver(
  element: Element,
  options: InViewportOptions,
  callback: InViewportCallback
): IntersectionObserver {
  const offset = options.offset || 0;
  
  // Convert offset to rootMargin format for IntersectionObserver
  // Positive offset = expand intersection area (trigger earlier)
  // Negative offset = shrink intersection area (trigger later)
  // rootMargin uses CSS margin syntax: "top right bottom left" or single value
  const rootMargin = offset !== 0 ? `${offset}px` : '0px';

  const observerOptions: IntersectionObserverInit = {
    root: options.container || null,
    rootMargin: rootMargin,
    threshold: 0,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry.target);
        // Unobserve after first intersection to match in-viewport behavior
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  return observer;
}

/**
 * Main in-viewport function
 * @param element - The element to watch
 * @param options - Options object with offset, container, debounce
 * @param callback - Callback function called when element enters viewport
 * @returns Watcher object with watch() and dispose() methods
 */
function inViewport(
  element: Element,
  options?: InViewportOptions | InViewportCallback,
  callback?: InViewportCallback
): Watcher | boolean {
  // Handle case where options is actually the callback
  if (typeof options === 'function') {
    callback = options;
    options = {};
  }

  if (!callback) {
    // Immediate check mode - return boolean
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewportHeight &&
      rect.right <= viewportWidth
    );
  }

  // Callback mode - create observer
  const opts = options || {};
  let observer: IntersectionObserver | null = null;
  let isDisposed = false;

  const watch = () => {
    if (isDisposed || !element) return;
    
    observer = createObserver(element, opts, callback!);
    observer.observe(element);
  };

  const dispose = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    isDisposed = true;
  };

  // Start watching immediately
  watch();

  return {
    watch,
    dispose,
  };
}

/**
 * MutationObserver-based watcher for dynamically added elements
 * Watches a container for new elements matching a selector
 */
export function watchForElements(
  container: Element,
  selector: string,
  options: InViewportOptions,
  callback: InViewportCallback
): () => void {
  const observers = new Map<Element, IntersectionObserver>();

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          
          // Check if the added node matches the selector
          if (element.matches(selector)) {
            const watcher = inViewport(element, options, callback) as Watcher;
            if (watcher && watcher.watch) {
              observers.set(element, createObserver(element, options, callback));
            }
          }

          // Check for nested elements matching the selector
          const matchingElements = element.querySelectorAll(selector);
          matchingElements.forEach((el) => {
            if (!observers.has(el)) {
              const watcher = inViewport(el, options, callback) as Watcher;
              if (watcher && watcher.watch) {
                observers.set(el, createObserver(el, options, callback));
              }
            }
          });
        }
      });

      // Clean up observers for removed nodes
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const observer = observers.get(element);
          if (observer) {
            observer.disconnect();
            observers.delete(element);
          }
        }
      });
    });
  });

  // Watch for added/removed child elements
  mutationObserver.observe(container, {
    childList: true,
    subtree: true,
  });

  // Also watch existing elements
  const existingElements = container.querySelectorAll(selector);
  existingElements.forEach((el) => {
    const watcher = inViewport(el, options, callback) as Watcher;
    if (watcher && watcher.watch) {
      observers.set(el, createObserver(el, options, callback));
    }
  });

  // Return cleanup function
  return () => {
    mutationObserver.disconnect();
    observers.forEach((observer) => observer.disconnect());
    observers.clear();
  };
}

export default inViewport;
